/**
 * Catalog course detail (public). Displays syllabus and enables
 * ENROLLMENT (local-first progress). Content arrives via REST (guest-first);
 * enrolling saves relation via sync (or locally for guest).
 */
import { type Component, createMemo, For, Show } from "solid-js";
import { createSafeQuery } from "@/lib/api";
import { msg } from "@lingui/core/macro";
import { BookOpen, CheckCircle2, Link2, PlayCircle, Star, Users } from "@fleet/icons";
import {
  courseQueryKeys,
  type CourseTreeDTO,
  enrollMutation,
  fetchCatalogCourse,
} from "@aula/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Link, useNavigate } from "@tanstack/solid-router";
import { SERVER_URL } from "@/lib/runtime-config";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@fleet/ui";
import { showToast } from "@/components/ui/toast";
import { canonicalUrl } from "@/lib/canonical-link";
import { useCourseWrite, useEnrollment } from "../../hooks/use-course-data";
import { QueryErrorState } from "@/components/ui/query-error-state";

export const CourseDetailView: Component<{ courseId: string }> = (props) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { drizzle, userId } = useCourseWrite();
  const enrollment = useEnrollment(() => props.courseId);
  const isEnrolled = () => Boolean(enrollment.data && !enrollment.data.deleted_at);

  const tree = createSafeQuery<CourseTreeDTO>(() => ({
    queryKey: courseQueryKeys.catalogCourse(props.courseId),
    queryFn: ({ signal }) => fetchCatalogCourse(SERVER_URL, props.courseId, { signal }),
    staleTime: 30_000,
  }));

  const lessonCount = createMemo(() =>
    (tree.data?.units ?? []).reduce((n, u) => n + u.lessons.length, 0),
  );

  const enroll = async () => {
    const db = drizzle();
    const uid = userId();
    if (!db || !uid) {
      return;
    }
    await enrollMutation(db, uid, props.courseId);
    showToast(t(msg`Enrolled — your progress is saved on this device`), "success");
    void navigate({ to: "/learn/$courseId", params: { courseId: props.courseId } });
  };

  return (
    <div class="mx-auto max-w-3xl space-y-6 p-4 md:p-6">
      <Link
        to="/catalog"
        class="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-accent"
      >
        ← {t(msg`Catalog`)}
      </Link>
      {/* P23/I3 · "no response" != "nothing here": on error `isLoading` is false,
          so the guard below passed through and the screen rendered the EMPTY state.
          We must WRAP the block, not chain another condition onto the same Show component
          (which would render both error AND skeleton). */}
      <Show when={tree.isError}>
        <QueryErrorState testId="course-detail-error" onRetry={() => void tree.refetch()} />
      </Show>
      <Show when={!tree.isError}>
        <Show when={!tree.isLoading} fallback={<Skeleton class="h-44 w-full rounded-3xl" />}>
          <Show
            when={tree.data}
            fallback={<p class="text-muted-foreground">{t(msg`Course not found.`)}</p>}
          >
            {(data) => (
              <>
                <div class="aula-mesh rounded-3xl border border-border p-6">
                  <div class="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-accent text-accent-foreground shadow-md shadow-accent/25">
                    <BookOpen class="h-7 w-7" />
                  </div>
                  <h1
                    class="font-display text-2xl font-extrabold tracking-tight md:text-3xl"
                    data-testid="course-title"
                  >
                    {data().course.title}
                  </h1>
                  <Show when={data().course.description}>
                    <p class="mt-2 text-muted-foreground">{data().course.description}</p>
                  </Show>
                  <div class="mt-4 flex flex-wrap items-center gap-2">
                    <span class="inline-flex items-center gap-1.5 rounded-full bg-accent/12 px-3 py-1 text-xs font-semibold text-accent">
                      <BookOpen class="h-3.5 w-3.5" />{" "}
                      {t(msg`{count} lessons`, { count: lessonCount() })}
                    </span>
                    <span class="inline-flex items-center gap-1.5 rounded-full bg-joy-sky/15 px-3 py-1 text-xs font-semibold text-joy-sky">
                      <Users class="h-3.5 w-3.5" />{" "}
                      {t(msg`{count} enrolled`, { count: data().enrolledCount })}
                    </span>
                  </div>
                  <div class="mt-5 flex flex-wrap items-center gap-3">
                    <Show
                      when={!isEnrolled()}
                      fallback={
                        <Button
                          data-testid="course-continue"
                          onClick={() =>
                            navigate({
                              to: "/learn/$courseId",
                              params: { courseId: props.courseId },
                            })
                          }
                          class="gap-2 rounded-2xl px-6 font-bold shadow-md shadow-accent/25"
                        >
                          <PlayCircle class="h-4 w-4" /> {t(msg`Continue`)}
                        </Button>
                      }
                    >
                      <Button
                        data-testid="course-enroll"
                        onClick={enroll}
                        class="gap-2 rounded-2xl px-6 font-bold shadow-md shadow-accent/25"
                      >
                        <CheckCircle2 class="h-4 w-4" /> {t(msg`Enroll for free`)}
                      </Button>
                    </Show>
                    <Button
                      variant="outline"
                      data-testid="course-copy-link-button"
                      class="gap-2 rounded-2xl px-4 font-semibold border-border"
                      onClick={() => {
                        void (async () => {
                          try {
                            await navigator.clipboard.writeText(canonicalUrl());
                            showToast(t(msg`Link copied`), "success");
                          } catch {
                            showToast(t(msg`Couldn't copy the link`), "error");
                          }
                        })();
                      }}
                    >
                      <Link2 class="h-4 w-4" /> {t(msg`Copy link`)}
                    </Button>
                  </div>
                </div>

                {/* Temario */}
                <div class="space-y-4" data-testid="course-syllabus">
                  <For each={data().units}>
                    {(u) => (
                      <div class="rounded-2xl border border-border bg-surface p-4 shadow-sm">
                        <h3 class="font-display font-bold">{u.unit.title}</h3>
                        <ul class="mt-2 space-y-1">
                          <For each={u.lessons}>
                            {(l) => (
                              <li class="flex items-center justify-between rounded-xl px-2 py-2 text-sm transition-colors hover:bg-accent/5">
                                <span class="font-medium">{l.lesson.title}</span>
                                <span class="flex items-center gap-2 text-xs text-muted-foreground">
                                  <span>{t(msg`{count} blocks`, { count: l.blocks.length })}</span>
                                  <span class="inline-flex items-center gap-1 font-bold text-joy-sun-foreground">
                                    <Star class="h-3 w-3 fill-current text-joy-sun" />
                                    {l.lesson.xpReward}
                                  </span>
                                </span>
                              </li>
                            )}
                          </For>
                        </ul>
                      </div>
                    )}
                  </For>
                </div>
              </>
            )}
          </Show>
        </Show>
      </Show>
    </div>
  );
};
